import { User, Session, AuthError } from "@supabase/supabase-js";
import { createContext, useContext, useEffect, useState, ReactNode, useCallback, useMemo } from "react";
import { useNavigate } from "react-router";

import { supabase } from "@/integrations/supabase/client";
import { logAuthEvent } from "@/lib/auto-activity-logger";
import { useAuthRole } from "@/hooks/useAuthRole";
import { useWebshopAccess } from "@/hooks/useWebshopAccess";

type UserRole = 'admin' | 'partner' | 'customer' | null;

interface AuthContextType {
  user: User | null;
  session: Session | null;
  userRole: UserRole;
  accessibleWebshops: number[];
  isAdmin: boolean;
  isPartner: boolean;
  isCustomer: boolean;
  signIn: (email: string, password: string) => Promise<{ error: AuthError | null }>;
  signUp: (email: string, password: string, fullName: string) => Promise<{ error: AuthError | null }>;
  signOut: () => Promise<void>;
  loading: boolean;
  refreshWebshopAccess: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [session, setSession] = useState<Session | null>(null);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  const { userRole, fetchUserRole } = useAuthRole();
  const { accessibleWebshops, fetchAccessibleWebshops } = useWebshopAccess();

  // Memoize role checks to prevent unnecessary recalculations
  const isAdmin = useMemo(() => userRole === 'admin', [userRole]);
  const isPartner = useMemo(() => userRole === 'partner', [userRole]);
  const isCustomer = useMemo(() => userRole === 'customer', [userRole]);

  const refreshWebshopAccess = useCallback(async () => {
    if (user) {
      await fetchAccessibleWebshops(user.id);
    }
  }, [user, fetchAccessibleWebshops]);

  useEffect(() => {
    let isMounted = true;

    // Get initial session
    supabase.auth.getSession().then(({ data: { session } }) => {
      if (!isMounted) return;
      
      setSession(session);
      setUser(session?.user ?? null);
      
      if (session?.user) {
        fetchUserRole(session.user.id);
        fetchAccessibleWebshops(session.user.id);
      }
      
      setLoading(false);
    });

    // Set up auth state listener
    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      (_event, session) => {
        if (!isMounted) return;
        
        setSession(session);
        setUser(session?.user ?? null);
        
        if (session?.user && _event !== 'INITIAL_SESSION') {
          fetchUserRole(session.user.id);
          fetchAccessibleWebshops(session.user.id);
        }
        
        if (!session) {
          setLoading(false);
        }
      }
    );

    return () => {
      isMounted = false;
      subscription.unsubscribe();
    };
  }, [fetchUserRole, fetchAccessibleWebshops]);

  const signIn = useCallback(async (email: string, password: string) => {
    const { error } = await supabase.auth.signInWithPassword({
      email,
      password,
    });

    if (error) {
      return { error };
    }

    await logAuthEvent('login', email);
    navigate("/");
    return { error: null };
  }, [navigate]);

  const signUp = useCallback(async (email: string, password: string, fullName: string) => {
    const { error } = await supabase.auth.signUp({
      email,
      password,
      options: {
        data: {
          full_name: fullName,
        },
      },
    });

    if (error) {
      return { error };
    }

    return { error: null };
  }, []);

  const signOut = useCallback(async () => {
    const userEmail = user?.email;
    await supabase.auth.signOut();
    if (userEmail) {
      await logAuthEvent('logout', userEmail);
    }
    navigate("/auth");
  }, [navigate, user]);

  const value = useMemo(() => ({
    user,
    session,
    userRole,
    accessibleWebshops,
    isAdmin,
    isPartner,
    isCustomer,
    signIn,
    signUp,
    signOut,
    loading,
    refreshWebshopAccess,
  }), [
    user,
    session,
    userRole,
    accessibleWebshops,
    isAdmin,
    isPartner,
    isCustomer,
    signIn,
    signUp,
    signOut,
    loading,
    refreshWebshopAccess,
  ]);

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
}
